import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Menu,
    Box,
    Typography,
    Divider,
    Switch,
    MenuItem,
    Button,
    Avatar,
    ListItemIcon,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';

const ProfileMenu = ({ anchorEl, onClose }) => {
    const navigate = useNavigate();
    const open = Boolean(anchorEl);
    const [userData, setUserData] = useState({});
    const [configOpen, setConfigOpen] = useState(false);

    useEffect(() => {
        const storedUserData = localStorage.getItem("userData");
        if (storedUserData) {
            setUserData(JSON.parse(storedUserData));
        }
    }, [open]);

    const handleLogout = () => {
        localStorage.clear();
        onClose();
        navigate('/');
    };

    const handleConfigOpen = () => {
        setConfigOpen(true);
        onClose();
    };

    const handleConfigClose = () => {
        setConfigOpen(false);
    };

    // Estilo común para los bloques de información dentro del modal premium
    const infoCardStyle = {
        p: 2,
        borderRadius: "12px",
        backgroundColor: "#F8FAFC",
        border: "1px solid #E2E8F0",
        display: "flex",
        alignItems: "center",
        gap: 2,
        height: "100%"
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                PaperProps={{
                    elevation: 4,
                    sx: { 
                        width: 300, 
                        p: 2, 
                        borderRadius: 3,
                        mt: 1.5,
                        border: "1px solid #E2E8F0",
                        overflow: "visible",
                        "&::before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: "background.paper",
                            transform: "translateY(-50%) rotate(45deg)",
                            zIndex: 0,
                        },
                    }
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 0.5 }}>
                    <Avatar 
                        alt={userData.nombre} 
                        src={userData.foto || undefined} 
                        sx={{ mr: 2, width: 44, height: 44, bgcolor: "#2FA4B8", fontWeight: "bold" }}
                    >
                        {userData.nombre ? userData.nombre.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: '#94A3B8', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Panel de Control
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="700" color="#0F172A" sx={{ lineHeight: 1.2, my: 0.2 }}>
                            {userData.nombre || 'Usuario'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                            {userData.rol || 'Usuario'}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                {/* Switch de notificaciones optimizado (sin botón No Molestar) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5, px: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="#475569" fontWeight="500">Notificaciones activas</Typography>
                        <Switch size="small" defaultChecked color="info" />
                    </Box>
                </Box>

                <Divider sx={{ mb: 0.5 }} />

                <MenuItem onClick={handleConfigOpen} sx={{ borderRadius: "8px", fontSize: "14px", color: "#334155", py: 1.2 }}>
                    <ListItemIcon>
                        <SettingsRoundedIcon fontSize="small" sx={{ color: "#64748B" }} />
                    </ListItemIcon>
                    Configuración de cuenta
                </MenuItem>
                
                <Divider sx={{ my: 0.5 }} />

                <MenuItem onClick={handleLogout} sx={{ borderRadius: "8px", fontSize: "14px", color: "#EF4444", py: 1.2 }}>
                    <ListItemIcon>
                        <LogoutRoundedIcon fontSize="small" sx={{ color: "#EF4444" }} />
                    </ListItemIcon>
                    Cerrar sesión
                </MenuItem>
            </Menu>

            {/* 🔝 Modal de Configuración de Cuenta Rediseñado (Premium) */}
            <Dialog 
                open={configOpen} 
                onClose={handleConfigClose} 
                fullWidth 
                maxWidth="sm" 
                PaperProps={{ 
                    sx: { 
                        borderRadius: 4,
                        boxShadow: "0px 20px 25px -5px rgba(0,0,0,0.1), 0px 10px 10px -5px rgba(0,0,0,0.04)"
                    } 
                }}
            >
                <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
                    <Typography variant="h5" fontWeight="800" color="#0F172A">
                        Configuración de Cuenta
                    </Typography>
                    <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>
                        Detalles de identidad y credenciales asignadas en el sistema.
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ px: 3, py: 2 }}>
                    <Box sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            
                            {/* Nombre Completo */}
                            <Grid item xs={12}>
                                <Box sx={infoCardStyle}>
                                    <Avatar sx={{ bgcolor: "#E0F2FE", color: "#0369A1" }}>
                                        <AccountCircleRoundedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="600" sx={{ textTransform: "uppercase" }}>Nombre Completo</Typography>
                                        <Typography variant="body1" fontWeight="600" color="#1E293B">{userData.nombre} {userData.apellido}</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Correo Electrónico */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={infoCardStyle}>
                                    <Avatar sx={{ bgcolor: "#F0FDF4", color: "#166534" }}>
                                        <EmailRoundedIcon />
                                    </Avatar>
                                    <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="600" sx={{ textTransform: "uppercase" }}>Correo Electrónico</Typography>
                                        <Typography variant="body2" fontWeight="600" color="#1E293B" noWrap>{userData.email || 'N/A'}</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Nombre de Usuario */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={infoCardStyle}>
                                    <Avatar sx={{ bgcolor: "#F5F3FF", color: "#5B21B6" }}>
                                        <BadgeRoundedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="600" sx={{ textTransform: "uppercase" }}>Usuario</Typography>
                                        <Typography variant="body2" fontWeight="600" color="#1E293B">{userData.usuario || 'N/A'}</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* RUT */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={infoCardStyle}>
                                    <Avatar sx={{ bgcolor: "#FFF7ED", color: "#9A3412" }}>
                                        <FingerprintRoundedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="600" sx={{ textTransform: "uppercase" }}>RUT</Typography>
                                        <Typography variant="body2" fontWeight="600" color="#1E293B">{userData.rut || 'N/A'}</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Empresa */}
                            <Grid item xs={12} sm={6}>
                                <Box sx={infoCardStyle}>
                                    <Avatar sx={{ bgcolor: "#EFF6FF", color: "#1E40AF" }}>
                                        <BusinessRoundedIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="caption" color="#94A3B8" fontWeight="600" sx={{ textTransform: "uppercase" }}>Empresa Asociada</Typography>
                                        <Typography variant="body2" fontWeight="600" color="#1E293B">{userData.empresa || 'N/A'}</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* ID y Rol de Sistema */}
                            <Grid item xs={12}>
                                <Box sx={{ ...infoCardStyle, justifyContent: "space-between", bgcolor: "#F1F5F9" }}>
                                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                        <Box>
                                            
                                            <Typography variant="caption" color="#64748B" display="block">DIRECCIÓN</Typography>
                                            <Typography variant="subtitle2" fontWeight="bold" color="#334155">#{userData.direccion_empresa || '0'}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign: "right" }}>
                                        <Typography variant="caption" color="#64748B" display="block">ROL</Typography>
                                        <Typography 
                                            variant="subtitle2" 
                                            fontWeight="bold" 
                                            sx={{ 
                                                color: userData.rol_idRol === 1 ? "#0284C7" : "#475569",
                                                bgcolor: userData.rol_idRol === 1 ? "#E0F2FE" : "#E2E8F0",
                                                px: 1.5,
                                                py: 0.2,
                                                borderRadius: "6px",
                                                display: "inline-block"
                                            }}
                                        >
                                            {userData.rol}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button 
                        onClick={handleConfigClose} 
                        variant="contained" 
                        fullWidth
                        sx={{ 
                            bgcolor: "#0F2F44", 
                            "&:hover": { bgcolor: "#1E3A5F" }, 
                            textTransform: "none", 
                            borderRadius: "10px",
                            py: 1.2,
                            fontWeight: "600"
                        }}
                    >
                        Cerrar Ventana
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ProfileMenu;